import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties } from 'react';
import { useLeadClose } from './useLeadClose.hook';
import './LeadCloseCelebration.styles.scss';

type tKind = 'sold' | 'resolved';

/** The prototype's celebrate(): 32 bills or 48 confetti pieces, about two seconds. */
const COUNT: Record<tKind, number> = { sold: 32, resolved: 48 };
const MOMENT_MS = 2200;
const CONFETTI = [
  '--ls-accent-primary-default',
  '--ls-accent-info-default',
  '--ls-accent-success-default',
  '--ls-accent-warning-default',
];

const reducedMotion = () =>
  !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

function Moment({ kind, onEnd }: { kind: tKind; onEnd: () => void }) {
  // Drawn once per moment: the pieces never re-render while they fall.
  const pieces = useMemo(
    () =>
      Array.from({ length: COUNT[kind] }, (_, i) => ({
        key: i,
        style: {
          '--x': `${2 + Math.round(Math.random() * 94)}%`,
          '--drift': `${Math.round((Math.random() - 0.5) * 180)}px`,
          '--spin': `${Math.round((Math.random() - 0.5) * 720)}deg`,
          '--dur': `calc(var(--ls-duration-xl) * ${(2.6 + Math.random()).toFixed(2)})`,
          '--delay': `calc(var(--ls-duration-md) * ${(Math.random() * 2.5).toFixed(2)})`,
          '--c': `var(${CONFETTI[i % CONFETTI.length]})`,
        } as CSSProperties,
        alt: kind === 'sold' ? i % 3 === 0 : i % 4 === 0,
      })),
    [kind],
  );

  useEffect(() => {
    const timer = setTimeout(onEnd, MOMENT_MS);
    // From the next turn, so the click that saved does not end it.
    const arm = setTimeout(() => {
      document.addEventListener('pointerdown', onEnd, true);
      document.addEventListener('keydown', onEnd, true);
    }, 0);
    return () => {
      clearTimeout(timer);
      clearTimeout(arm);
      document.removeEventListener('pointerdown', onEnd, true);
      document.removeEventListener('keydown', onEnd, true);
    };
  }, [onEnd]);

  return createPortal(
    <div className="LeadCloseCelebration" aria-hidden="true">
      {pieces.map((piece) =>
        kind === 'sold' ? (
          <span
            key={piece.key}
            className={`LeadCloseCelebration__Piece LeadCloseCelebration__Bill${piece.alt ? ' is-alt' : ''}`}
            // eslint-disable-next-line no-restricted-syntax -- per-piece random fall
            style={piece.style}
          >
            $
          </span>
        ) : (
          <span
            key={piece.key}
            className={`LeadCloseCelebration__Piece LeadCloseCelebration__Confetti${piece.alt ? ' is-dot' : ''}`}
            // eslint-disable-next-line no-restricted-syntax -- per-piece random fall
            style={piece.style}
          />
        ),
      )}
    </div>,
    document.body,
  );
}

/**
 * LEA-50 U18: bills fall on a created Vendido, confetti on a created Resuelto
 * (R14). Only in the tab that saved (it reads `lastSave`, never a frame), once
 * per result, never with reduced motion. Mounts only while it plays.
 */
export default function LeadCloseCelebration(): JSX.Element | null {
  const { lastSave } = useLeadClose();
  const [playing, setPlaying] = useState<{ id: string; kind: tKind } | null>(
    null,
  );
  const seenSave = useRef<string | null>(null);
  const seenResults = useRef(new Set<string>());

  useEffect(() => {
    if (!lastSave || lastSave.kind !== 'create') return;
    if (seenSave.current === lastSave.id) return;
    seenSave.current = lastSave.id;
    const { type, uuid } = lastSave.answer.result;
    // An idempotent replay answers with a result already celebrated.
    if (seenResults.current.has(uuid)) return;
    seenResults.current.add(uuid);
    if (type === 'lost' || reducedMotion()) return;
    setPlaying({ id: lastSave.id, kind: type });
  }, [lastSave]);

  const end = useMemo(() => () => setPlaying(null), []);
  if (!playing) return null;
  return <Moment key={playing.id} kind={playing.kind} onEnd={end} />;
}
